import { zodResolver } from '@hookform/resolvers/zod';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import TextField from '@mui/material/TextField';
import { useSnackbar } from 'notistack';
import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router';

import { useCreateSubscriptionMutation, useGetTenantsQuery } from 'src/apiClients';
import { findDefaultStoreIds } from 'src/apiClients/queries';
import { buildInstanceDetailSchema } from 'src/pages/instance-detail-page/schema';
import type { InstanceDetailFormValues } from 'src/pages/instance-detail-page/schema';
import { ROUTES } from 'src/utils/strings';

import { i18n } from './NewInstanceDialog.i18n';
import { useStyles } from './NewInstanceDialog.styles';

export interface NewInstanceDialogProps {
    open: boolean;
    onClose: () => void;
}

const EMPTY_VALUES: InstanceDetailFormValues = {
    subscriptionName: '',
    comment: '',
    salesforceLink: '',
    customerName: '',
    companyName: '',
    capacityGroupId: '',
    capacityGroupName: '',
};

/**
 * Creation form for a new instance (subscription). Mirrors the legacy admin
 * flow: the new subscription is attached to the default tenant's default
 * image/client stores, which the server does not set on its own.
 */
export const NewInstanceDialog = ({ open, onClose }: NewInstanceDialogProps) => {
    const intl = useIntl();
    const { classes } = useStyles();
    const { enqueueSnackbar } = useSnackbar();
    const navigate = useNavigate();

    const { data: tenants, isError: isTenantsError } = useGetTenantsQuery(undefined, {
        skip: !open,
    });
    const defaultStoreIds = useMemo(
        () => (tenants ? findDefaultStoreIds(tenants) : null),
        [tenants],
    );
    const storesUnavailable = isTenantsError || (!!tenants && !defaultStoreIds);

    const [createSubscription, { isLoading: isCreating }] = useCreateSubscriptionMutation();

    const schema = useMemo(
        () =>
            buildInstanceDetailSchema(
                intl.formatMessage(i18n.displayNameRequired),
                intl.formatMessage(i18n.capacityGroupIdInvalid),
            ),
        [intl],
    );

    const {
        register,
        handleSubmit,
        reset,
        formState: { errors, isValid },
    } = useForm<InstanceDetailFormValues>({
        resolver: zodResolver(schema),
        defaultValues: EMPTY_VALUES,
        mode: 'onChange',
    });

    const handleClose = () => {
        reset(EMPTY_VALUES);
        onClose();
    };

    const onSubmit = handleSubmit(async (values) => {
        if (!defaultStoreIds) return;
        try {
            const created = await createSubscription({ ...values, ...defaultStoreIds }).unwrap();
            enqueueSnackbar(intl.formatMessage(i18n.createSuccess), { variant: 'success' });
            handleClose();
            if (created.id) {
                navigate(`${ROUTES.INSTANCES}/${created.id}`);
            }
        } catch {
            enqueueSnackbar(intl.formatMessage(i18n.createError), { variant: 'error' });
        }
    });

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
            automation-id="new-instance-dialog"
        >
            <DialogTitle>{intl.formatMessage(i18n.title)}</DialogTitle>
            <DialogContent>
                {storesUnavailable && (
                    <Alert severity="error" automation-id="new-instance-stores-error">
                        {intl.formatMessage(i18n.defaultStoresError)}
                    </Alert>
                )}
                <Box
                    component="form"
                    className={classes.fields}
                    onSubmit={onSubmit}
                    automation-id="new-instance-form"
                    noValidate
                >
                    <TextField
                        {...register('subscriptionName')}
                        label={intl.formatMessage(i18n.displayName)}
                        error={!!errors.subscriptionName}
                        helperText={errors.subscriptionName?.message}
                        fullWidth
                        required
                        autoFocus
                        automation-id="new-instance-field-display-name"
                    />
                    <TextField
                        {...register('customerName')}
                        label={intl.formatMessage(i18n.accountName)}
                        fullWidth
                        automation-id="new-instance-field-account-name"
                    />
                    <TextField
                        {...register('companyName')}
                        label={intl.formatMessage(i18n.legalName)}
                        fullWidth
                        automation-id="new-instance-field-legal-name"
                    />
                    <TextField
                        {...register('salesforceLink')}
                        label={intl.formatMessage(i18n.salesforceId)}
                        fullWidth
                        automation-id="new-instance-field-salesforce-link"
                    />
                    <TextField
                        {...register('capacityGroupName')}
                        label={intl.formatMessage(i18n.capacityGroupName)}
                        fullWidth
                        automation-id="new-instance-field-capacity-group-name"
                    />
                    <TextField
                        {...register('capacityGroupId')}
                        label={intl.formatMessage(i18n.capacityGroupId)}
                        error={!!errors.capacityGroupId}
                        helperText={errors.capacityGroupId?.message}
                        fullWidth
                        automation-id="new-instance-field-capacity-group-id"
                    />
                    <TextField
                        {...register('comment')}
                        label={intl.formatMessage(i18n.comment)}
                        fullWidth
                        automation-id="new-instance-field-comment"
                    />
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} automation-id="new-instance-cancel">
                    {intl.formatMessage(i18n.cancel)}
                </Button>
                <Button
                    variant="contained"
                    onClick={onSubmit}
                    disabled={!isValid || isCreating || !defaultStoreIds}
                    automation-id="new-instance-create"
                >
                    {intl.formatMessage(i18n.create)}
                </Button>
            </DialogActions>
        </Dialog>
    );
};
